import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { format } from "date-fns";
import { Loader2, AlertCircle } from "lucide-react";
import {
  AddressFormat,
  formatAddress,
  formatCurrency,
  sendAuthenticatedRequest,
} from "../common/functions";
import useFirebaseAuthentication from "../common/hooks/useFirebaseAuthentication";
import { formatDimensions } from "./InventorySearch/helpers";
import { OrderDetails } from "../common/models/order";
import { useDimensionsUnit } from "../buyerStateDefs";
import { Timestamp } from "firebase/firestore";
import PaymentForm from "./PaymentForm";
import { useBuyerExperience } from "../buyerCare";
import { ActivityTrail, actorLabel } from "./ActivityTrail";
import { persistShoppingCart } from "../cart/serverCart";

type ActivePayment = {
  orderId: string;
  clientSecret: string;
  totalPrice: number;
  currency: string;
};

const placedOn = (createdAt: OrderDetails["createdAt"]) => {
  if (!createdAt) return "Unknown date";
  return format(
    createdAt instanceof Timestamp && typeof createdAt.toDate === "function"
      ? createdAt.toDate()
      : new Date(createdAt as unknown as string),
    "PPpp",
  );
};

const BuyerOrderCard = ({
  order,
  productName,
  canPay,
  payingOrderId,
  payError,
  activePayment,
  onStartPayment,
  onPaymentSuccess,
  onReorder,
  viewerEmail,
}: {
  order: OrderDetails;
  productName: (product: any) => string;
  canPay: boolean;
  payingOrderId: string | null;
  payError: string | null;
  activePayment: ActivePayment | null;
  onStartPayment: (order: OrderDetails) => void;
  onPaymentSuccess: (orderId: string) => void;
  onReorder?: (order: OrderDetails) => void;
  viewerEmail?: string | null;
}) => (
  <li className="bg-white shadow-sm rounded-2xl border border-gray-200 p-6 list-none">
    <div className="flex justify-between items-start mb-4">
      <div>
        <h2 className="text-lg font-medium text-gray-900">
          Order #{order.orderId ?? "N/A"}
        </h2>
        <p className="text-sm text-gray-500">
          Placed on {placedOn(order.createdAt)}
          {order.placedBy
            ? ` by ${actorLabel(order.placedBy, viewerEmail)} (${order.placedBy.email})`
            : ""}
        </p>
        <ActivityTrail
          className="mt-1"
          activity={order.activity}
          viewerEmail={viewerEmail}
        />
      </div>
      <span
        className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
          order.orderStatus === "paid"
            ? "bg-green-100 text-green-800"
            : order.orderStatus === "pending"
              ? "bg-yellow-100 text-yellow-800"
              : "bg-gray-100 text-gray-800"
        }`}
      >
        {order.orderStatus ?? "unknown"}
      </span>
    </div>

    <div className="space-y-4">
      {order.cartItems.map((item, idx) => (
        <div
          key={idx}
          className="border border-gray-100 p-4 rounded-lg bg-gray-50"
        >
          <div className="text-sm text-gray-700 font-semibold mb-1">
            {productName(item.product) ?? "Unnamed product"}
          </div>
          <div className="text-sm text-gray-500">
            Quantity:{" "}
            {item.quantityRequiredString && item.quantityRequiredString !== ""
              ? item.quantityRequiredString
              : `${item.quantityRequired?.value ?? "-"} ${
                  item.quantityRequired?.unit ?? ""
                }`}
          </div>
          <div className="text-sm text-gray-500">
            Delivery Address:{" "}
            {formatAddress(item.deliveryAddress, AddressFormat.Full)}
          </div>
          {item.millCertRequired && (
            <div className="text-sm text-gray-500">✅ Mill Cert Required</div>
          )}
          {item.notes && (
            <div className="mt-2 rounded border border-amber-200 bg-amber-50 px-2 py-1.5 text-sm text-amber-900">
              Notes: {item.notes}
            </div>
          )}
        </div>
      ))}
    </div>

    {order.totalPrice !== undefined && (
      <div className="mt-4 text-right font-medium text-gray-900">
        Total: {formatCurrency(order.totalPrice, order.currency)}
      </div>
    )}

    {onReorder && order.orderStatus === "paid" && (
      <div className="mt-3 text-right">
        <button
          type="button"
          onClick={() => onReorder(order)}
          className="rounded-full border-2 border-[#00062e] px-5 py-1.5 text-sm font-bold text-[#00062e] hover:bg-slate-100"
          title="Put these same items and quantities in your cart"
        >
          Reorder these items
        </button>
      </div>
    )}

    {canPay &&
      order.orderStatus === "pending" &&
      order.paymentMethod !== "wire_transfer" && (
        <div className="mt-4 border-t border-gray-100 pt-4">
          {activePayment && activePayment.orderId === order.orderId ? (
            <PaymentForm
              clientSecret={activePayment.clientSecret}
              totalPrice={activePayment.totalPrice}
              currency={activePayment.currency}
              onPaymentSuccess={() =>
                onPaymentSuccess(order.orderId as string)
              }
            />
          ) : (
            <>
              <button
                type="button"
                onClick={() => onStartPayment(order)}
                disabled={payingOrderId === order.orderId}
                className="rounded-full bg-[#00062e] hover:bg-slate-900 text-white font-bold w-full py-2 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {payingOrderId === order.orderId
                  ? "Starting payment..."
                  : "Complete Payment"}
              </button>
              {payingOrderId === order.orderId && payError && (
                <div className="mt-2 text-sm text-red-600 flex items-center gap-1">
                  <AlertCircle className="h-4 w-4" />
                  {payError}
                </div>
              )}
            </>
          )}
        </div>
      )}
  </li>
);

export default function MyOrders() {
  const { authUser } = useFirebaseAuthentication();
  // Kono operators: /orders?scope=all lists every customer's orders.
  const [searchParams] = useSearchParams();
  const scope: "mine" | "all" = searchParams.get("scope") === "all" ? "all" : "mine";
  const [orders, setOrders] = useState<OrderDetails[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [payingOrderId, setPayingOrderId] = useState<string | null>(null);
  const [payError, setPayError] = useState<string | null>(null);
  const [activePayment, setActivePayment] = useState<ActivePayment | null>(
    null,
  );

  const dimensionUnit = useDimensionsUnit((state) => state.dimensionsUnit);
  const experience = useBuyerExperience();
  const navigate = useNavigate();

  // One-click reorder: the same lines and quantities back in the cart, with
  // the stale freight quotes dropped so the cart re-rates them.
  const reorder = (order: OrderDetails) => {
    const cartItems = (order.cartItems || []).map((item) => {
      const { shippingCost, shippingOptions, selectedShippingOption, ...rest } =
        item as any;
      return rest;
    });
    persistShoppingCart({ cartItems });
    navigate("/cart");
  };

  const startPayment = async (order: OrderDetails) => {
    if (!order.orderId) {
      return;
    }
    setPayError(null);
    setPayingOrderId(order.orderId);
    setActivePayment(null);
    try {
      const response = await sendAuthenticatedRequest(
        `${process.env.REACT_APP_USER_API_BASE_URL}/orders/${order.orderId}/pay`,
        { method: "POST" },
      );
      if (!response.ok) {
        const message = await response.text();
        throw new Error(message || `Failed to start payment (${response.status})`);
      }
      const data = await response.json();
      if (!data.clientSecret) {
        throw new Error("Payment could not be initialized. Please try again.");
      }
      setActivePayment({
        orderId: order.orderId,
        clientSecret: data.clientSecret,
        totalPrice: data.totalPrice,
        currency: data.currency || order.currency || "USD",
      });
    } catch (err) {
      console.error("Failed to start payment:", err);
      setPayError(
        err instanceof Error ? err.message : "Failed to start payment.",
      );
      setPayingOrderId(null);
    }
  };

  const handlePaymentSuccess = (orderId: string) => {
    setOrders((prev) =>
      prev.map((o) =>
        o.orderId === orderId ? { ...o, orderStatus: "paid" } : o,
      ),
    );
    setActivePayment(null);
    setPayingOrderId(null);
  };

  useEffect(() => {
    const fetchOrders = async () => {
      setLoading(true);
      setError(null);
      sendAuthenticatedRequest(
        `${process.env.REACT_APP_USER_API_BASE_URL}/orders${scope === "all" ? "?scope=all" : ""}`,
        {},
      )
        .then((response) => response.json())
        .then((response) => {
          if (!response.orders) {
            throw new Error("No orders found");
          }
          return response;
        })
        .then((response) => {
          setOrders(response.orders);
        })
        .catch((err) => {
          console.error("Failed to fetch orders:", err);
          setError("Failed to load your orders. Please try again.");
        })
        .finally(() => setLoading(false));
    };

    if (!authUser) {
      return;
    }
    fetchOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authUser, scope]);

  const productName = (product: any) => {
    const mats = product?.material?.dbMaterials || [];
    if (mats.length === 0) {
      return product?.name || "Unnamed Product";
    }
    const dims = formatDimensions(product, dimensionUnit);
    const shape = product?.shape || "";

    return `${mats[0]} ${shape} ${dims}`;
  };

  if (loading) {
    return (
      <div className="flex justify-center py-10">
        <Loader2 className="h-6 w-6 animate-spin text-gray-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-red-600 text-center mt-8 flex justify-center items-center gap-2">
        <AlertCircle className="h-5 w-5" />
        {error}
      </div>
    );
  }

  return (
    <>
      <h1 className="text-2xl font-bold text-gray-900 mb-6">
        {scope === "all" ? "All Orders (Kono team)" : "My Orders"}
      </h1>
      {orders.length === 0 ? (
        <p className="text-gray-500">You haven't placed any orders yet.</p>
      ) : (
        <ul className="space-y-6">
          {orders.map((order) => (
            <BuyerOrderCard
              key={order.orderId}
              order={order}
              productName={productName}
              canPay
              payingOrderId={payingOrderId}
              payError={payError}
              activePayment={activePayment}
              onStartPayment={startPayment}
              onPaymentSuccess={handlePaymentSuccess}
              onReorder={experience.oneClickReorder ? reorder : undefined}
              viewerEmail={authUser?.email}
            />
          ))}
        </ul>
      )}
    </>
  );
}

/** One order, same card the customer sees on My Orders. */
export function OrderDetail() {
  const { id } = useParams();
  const { authUser, authIsLoading } = useFirebaseAuthentication();
  const [order, setOrder] = useState<OrderDetails | null>(null);
  const [error, setError] = useState("");
  const dimensionUnit = useDimensionsUnit((state) => state.dimensionsUnit);

  useEffect(() => {
    if (!id || authIsLoading) return;
    let cancelled = false;
    const load = async () => {
      try {
        if (authUser) await authUser.getIdToken();
        const response = await sendAuthenticatedRequest(
          `${process.env.REACT_APP_USER_API_BASE_URL}/orders/${id}`,
          {},
        );
        if (!response.ok) {
          throw new Error(
            response.status === 404
              ? "Order not found"
              : "Could not load this order.",
          );
        }
        const body = await response.json();
        if (!cancelled) setOrder(body.order);
      } catch (err) {
        if (!cancelled) {
          setError(
            err instanceof Error ? err.message : "Could not load this order.",
          );
        }
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [id, authUser, authIsLoading]);

  const productName = (product: any) => {
    const mats = product?.material?.dbMaterials || [];
    if (mats.length === 0) {
      return product?.name || "Unnamed Product";
    }
    return `${mats[0]} ${product?.shape || ""} ${formatDimensions(
      product,
      dimensionUnit,
    )}`;
  };

  // The buyer on the order, or a coworker at the order's business domain
  // (the server sets companyDomain only for business mail hosts).
  const viewerDomain = (authUser?.email || "").toLowerCase().split("@")[1] || "";
  const ownOrder =
    !!authUser?.email &&
    !!order?.email &&
    (authUser.email.toLowerCase() === order.email.toLowerCase() ||
      (!!order.companyDomain && order.companyDomain === viewerDomain));

  return (
    <div className="mx-auto mt-8 mb-16 max-w-4xl px-4">
      <h1 className="text-2xl font-bold text-gray-900">My Orders</h1>
      {error && /not found/i.test(error) && !authUser ? (
        <div className="mt-4 max-w-xl rounded-xl border border-blue-200 bg-blue-50 p-4 text-sm text-gray-700">
          <p className="font-medium text-[#00062e]">
            This order is private — sign in to open it.
          </p>
          <p className="mt-1">
            <Link className="text-blue-700 hover:underline" to="/signin">
              Sign in
            </Link>{" "}
            with the customer account, or a kono.ai account, and it will open
            here exactly as the customer sees it.
          </p>
        </div>
      ) : error && /not found/i.test(error) && authUser ? (
        <div className="mt-4 max-w-xl rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-gray-700">
          <p className="font-medium text-[#00062e]">
            This order is not available to {authUser.email || "this account"}.
          </p>
        </div>
      ) : (
        error && <p className="mt-3 text-sm text-red-600">{error}</p>
      )}
      {!order && !error && (
        <p className="mt-4 text-sm text-gray-500">Loading the order...</p>
      )}
      {order && (
        <ul className="mt-6 space-y-6">
          <BuyerOrderCard
            order={order}
            productName={productName}
            canPay={ownOrder}
            payingOrderId={null}
            payError={null}
            activePayment={null}
            onStartPayment={() => undefined}
            onPaymentSuccess={() => undefined}
            viewerEmail={authUser?.email}
          />
        </ul>
      )}
    </div>
  );
}
